import axios from 'axios';
import { apiClient, IS_DEV } from './apiClient.ts';

export interface ContentItem {
  id: string;
  content_type: 'text' | 'image';
  content_key: string;
  content_value: string;
  created_at?: string;
  updated_at?: string;
}

class ContentService {
  private apiClient = apiClient;

  async getAllContent(): Promise<ContentItem[]> {
    try {
      const response = await this.apiClient.get('/api/content');
      return response.data;
    } catch (error) {
      if (IS_DEV) console.error('Error fetching content:', error);
      throw error;
    }
  }

  async getContentByKey(contentKey: string): Promise<ContentItem | null> {
    try {
      const response = await this.apiClient.get(`/api/content/${contentKey}`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return null;
      }
      if (IS_DEV) console.error('Error fetching content by key:', error);
      throw error;
    }
  }

  async updateContent(contentKey: string, contentValue: string, contentType: 'text' | 'image' = 'text'): Promise<ContentItem> {
    try {
      const response = await this.apiClient.put(`/api/content/${contentKey}`, {
        content_value: contentValue,
        content_type: contentType,
      });
      return response.data;
    } catch (error) {
      if (IS_DEV) console.error('Error updating content:', error);
      throw error;
    }
  }

  async createContent(contentKey: string, contentValue: string, contentType: 'text' | 'image' = 'text'): Promise<ContentItem> {
    try {
      const response = await this.apiClient.post('/api/content', {
        content_key: contentKey,
        content_value: contentValue,
        content_type: contentType,
      });
      return response.data;
    } catch (error) {
      if (IS_DEV) console.error('Error creating content:', error);
      throw error;
    }
  }

  async deleteContent(contentKey: string): Promise<void> {
    try {
      await this.apiClient.delete(`/api/content/${contentKey}`);
    } catch (error) {
      if (IS_DEV) console.error('Error deleting content:', error);
      throw error;
    }
  }

  async uploadImage(file: File): Promise<string> {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await this.apiClient.post('/api/upload/image', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      // Return the uploaded image URL
      return response.data.url || response.data.image_url;
    } catch (error) {
      if (IS_DEV) console.error('Error uploading image:', error);
      throw error;
    }
  }
}

export const contentService = new ContentService();
